import React, { useState } from "react";
import gql from "graphql-tag";
import { Flex, Box, TouchableOpacity, Text } from "@atoms";
import { constants } from "@services";
import { FundsPageEarningsFundraisersListItem } from "../FundsPageEarningsFundraisersListItem";
import { FundsPageEarningsFundraisersListFundraisersFragment } from "./__generated__/FundsPageEarningsFundraisersListFundraisersFragment";
import { FundsPageEarningsFundraisersListViewerFundraiserFragment } from "./__generated__/FundsPageEarningsFundraisersListViewerFundraiserFragment";

// == Types ================================================================

interface IProps {
  fundraisers: FundsPageEarningsFundraisersListFundraisersFragment[];
  viewerFundraiser: FundsPageEarningsFundraisersListViewerFundraiserFragment | null;
  privacyLevel: string;
}

FundsPageEarningsFundraisersList.fragments = {
  viewerFundraiser: gql`
    fragment FundsPageEarningsFundraisersListViewerFundraiserFragment on Campaign {
      id
      viewerFundraiser @include(if: $hasCurrentUser) {
        id
        name
        userId
      }
      ...FundsPageEarningsFundraisersListItemPrivacyLevel
    }
    ${FundsPageEarningsFundraisersListItem.fragments.privacyLevel}
  `,
  fundraisers: gql`
    fragment FundsPageEarningsFundraisersListFundraisersFragment on Fundraiser {
      id
      ...FundsPageEarningsFundraisersListItemFundraiserFragment
    }
    ${FundsPageEarningsFundraisersListItem.fragments.fundraiser}
  `,
};

// == Constants ============================================================

const { PAGINATION_FUNDRAISERS_LIMIT } = constants;

// == Component ============================================================

export default function FundsPageEarningsFundraisersList({
  fundraisers,
  viewerFundraiser,
  privacyLevel,
}: IProps) {
  const [newLimit, setNewLimit] = useState(PAGINATION_FUNDRAISERS_LIMIT);

  const isMoreFundraisers = fundraisers.length > newLimit;
  const remainingFundraisersCount = fundraisers.length - newLimit;

  const fundraisersPaginator = () => {
    return fundraisers
      .sort((a, b) => b.amountRaised.dollarsAmount - a.amountRaised.dollarsAmount)
      .slice(0, newLimit);
  };

  const renderRemainingFundraisers = () => {
    return `+${remainingFundraisersCount} more`;
  };

  const onTouch = () => {
    setNewLimit(newLimit + PAGINATION_FUNDRAISERS_LIMIT);
  };

  return (
    <Flex bg="white" flexDirection="column" justifyContent="flex-start" width={1}>
      {fundraisersPaginator().map((fundraiser) => (
        <FundsPageEarningsFundraisersListItem
          fundraiser={fundraiser}
          isViewerFundraiser={fundraiser.id === viewerFundraiser?.id}
          key={fundraiser.id}
          privacyLevel={privacyLevel}
        />
      ))}
      {isMoreFundraisers && (
        <Box px={[3, 4]} py={[2, 3]} width={1}>
          <TouchableOpacity variant="clear" onTouch={onTouch}>
            <Text color="primary">{renderRemainingFundraisers()}</Text>
          </TouchableOpacity>
        </Box>
      )}
    </Flex>
  );
}

// == Styles ===============================================================
